import PageApp from 'pageApp';
import TrackingPixel from '@component/common/src/components/TrackingPixel';
import TagManagerUtil from '@component/common/src/util/TagManagerUtil';
import { format } from '@component/common/src/util/TealiumFormattingUtil';
import Coremetrics from '@component/common/src/util/Coremetrics';
import ProsTrackingHelper from '../util/ProsTrackingHelper';
import Util from '../util/Util';
import ATLTemplate from '../templates/partials/add-to-list-container.hbs';
import List from '../data/List';
import GetProperty from '../util/PropertyGetter';
import Constants from '../util/Constants';

const PIXEL_PRESENTED = 'PixelPresented';
const SCROLLED = 'scrolled';

const pixelHostToAppend = (() => {
  const { protocol, host } = window.location;
  return `${protocol}//${host}`;
})();

export default class ProsEventHandler {
  constructor() {
    this.presented = {};
  }

  getProductIdsWithPos(pageIdsPos, requester, zone) {
    return pageIdsPos.filter(({ id }) => {
      const uniqueId = `${requester}-${zone}-${id}`;
      const notPresented = !this.presented[uniqueId];
      if (notPresented) {
        this.presented[uniqueId] = true;
      }
      return notPresented;
    }).map(({ id, pos }) => `${id}_Pos${pos}`).join('|');
  }

  sendPixelEvent({
    pageIdsPos, currentPage, config, content,
  }) {
    if (!pageIdsPos || !currentPage || !config) {
      return;
    }
    const { requester, zone, encodedChoiceId } = config;
    if (!requester || !zone || !encodedChoiceId) {
      return;
    }
    const productIdsWithPos = this.getProductIdsWithPos(pageIdsPos, requester, zone);
    if (!productIdsWithPos) {
      return;
    }

    const eventType = (currentPage === 1) ? PIXEL_PRESENTED : SCROLLED;

    let eventData = `pList=${productIdsWithPos}&r=${requester}&c=${zone}&rType=${eventType}&vId=${Util.visitorId()}`;

    const cgId = ProsTrackingHelper.getCGIDParamter(null, encodedChoiceId); // Control Group ID
    const dId = ProsTrackingHelper.getBatchId(null, encodedChoiceId); // Batch ID
    const hId = ProsTrackingHelper.getHeaderId(null, encodedChoiceId); // Header ID
    const cId = Util.customerId();

    const cmCoreIdPromise = ProsTrackingHelper.getCmCoreId6();

    if (cId) { eventData = `${eventData}&cId=${cId}`; }
    if (cgId) { eventData = `${eventData}&cgId=${cgId}`; }
    if (hId) { eventData = `${eventData}&hId=${hId}`; }
    if (dId) { eventData = `${eventData}&dId=${dId}`; }
    if (content && content.contentId) { eventData = `${eventData}&contentId=${content.contentId}`; }

    cmCoreIdPromise.then((coreId6) => {
      if (coreId6) { eventData = `${eventData}&coreId=${coreId6}`; }

      TrackingPixel.track(encodeURI(eventData), {
        pixelHost: pixelHostToAppend,
        pathToPixel: '/EventsWar/events/pixel/customeraction',
      });
    });
  }

  static bindThumbnailOverHandler(prosContainer, prosZonesAddToListEnabled = true) {
    const allThumbnails = prosContainer.find('.productThumbnailImage');
    const killSwitch = PageApp.getMeta('properties');
    let length = allThumbnails.length - 1;
    let smallThumbnail = false;

    if (allThumbnails.length > 0) {
      const thumbnailImgEL = allThumbnails.first().find('img');
      if (thumbnailImgEL && thumbnailImgEL.innerWidth() < 160) {
        smallThumbnail = true;
      }
    }

    const template = ATLTemplate({ smallThumbnail });
    while (length >= 0) {
      const lastThumbnail = allThumbnails.eq(length);
      if (lastThumbnail.find('.altContainer').length === 0 &&
          !Util.internationalOrRegistryMode() &&
          killSwitch && killSwitch.prosZonesAddToListEnabled &&
          prosZonesAddToListEnabled
      ) {
        lastThumbnail.append(template);
        List.updateHeart(lastThumbnail);
      }
      length -= 1;
    }
  }

  /**
     * When user clicks on a product-thumbnail...
     * @component/product-thumbnail triggers 'component::productThumbnail::clicked' event with productId.
     *
     *
     * @param $element
     * @param choiceId
     * @param collection
     * @param headerText
     * @param zone
     */
  static registerAnalytics({
    $el, choiceId, collection, headerText, zone,
  }) {
    $el.on('component::productThumbnail::clicked', (event, productId) => {
      const model = choiceId.substring(6, 9);
      const position = collection.get(productId).get('position');
      TagManagerUtil.fireTag('link', {
        event_name: 'recommendation link click',
        recommendation_model: model,
        recommendation_position: position !== undefined ? position.toString() : '',
        recommendation_title: headerText,
        recommendation_zone: zone,
      });
    });
  }

  static registerHeartingAdobeAnalytics({
    $el, choiceId, collection, headerText, zone,
  }) {
    $el.on('component::addToList::toggle', (event, eventTagProduct) => {
      const { eventName, productId, eventType } = eventTagProduct;
      const productModel = collection.get(productId);
      if (productModel) {
        const modelData = {
          event_name: eventName,
          event_type: eventType,
          recommendation_model: choiceId.substring(6, 9),
          recommendation_position: productModel.get('position'),
          recommendation_title: headerText,
          recommendation_zone: zone,
        };
        let productData = GetProperty(productModel.get('product'), 'meta.analytics.data', {});
        productData = {
          product_id: format(productData.productID || productData.product_id),
          product_name: format(productData.product_name),
          product_original_price: format(productData.product_original_price),
          product_price: format(productData.product_price),
        };
        TagManagerUtil.fireTag('link', Object.assign(modelData, productData));
      }
    });
  }

  static registerLockActionAnalytics({
    $el, choiceId, collection, zone,
  }) {
    $el.on('component::comparison-panel::lock-action', (event, eventTagProduct) => {
      const { eventName, productId } = eventTagProduct;
      const productModel = collection.get(productId);
      if (productModel) {
        const modelData = {
          event_name: eventName,
          recommendation_model: choiceId.substring(6, 9),
          recommendation_position: `${productModel.get('position')}`,
          recommendation_title: Constants.COMPARISON_TABLE_HEADER_TEXT,
          recommendation_zone: zone,
        };
        let productData = GetProperty(productModel.get('product'), 'meta.analytics.data', {});
        productData = {
          product_id: format(productData.productID || productData.product_id),
          product_name: format(productData.product_name),
          product_original_price: format(productData.product_original_price),
          product_price: format(productData.product_price),
        };
        TagManagerUtil.fireTag('link', Object.assign(modelData, productData));
      }
    });
  }

  static registerChevronAnalytics(carousel) {
    if (carousel && carousel.buttons && carousel.buttons.length > 1) {
      carousel.buttons[0].addEventListener('click', ProsEventHandler.triggerChevronAnalytics);
      carousel.buttons[1].addEventListener('click', ProsEventHandler.triggerChevronAnalytics);
    }
  }

  static triggerPageNavAnalytics() {
    Coremetrics.createLinkClickTag({
      href: 'cm_sp=pros_scroll-_-top-_-chevrons',
    });
  }

  static triggerTabSelectionAnalytics(targetZone) {
    const serial = targetZone.charAt(targetZone.length - 1);
    Coremetrics.createLinkClickTag({
      href: `cm_sp=pros-_-panel-_-multitab_${serial}`,
    });
  }

  static triggerChevronAnalytics() {
    Coremetrics.createLinkClickTag({
      href: 'cm_sp=pros_scroll-_-side-_-navigation_scroll',
    });
  }
}
